import { summarizeSession } from '@muzly/shared';
import type { ScoredFrame, TargetMelody } from '@muzly/shared';
import type { FrameScore } from '@muzly/shared';

import { InfoTip } from './InfoTip';
import {
  centsToOffsetPct,
  melodyProgress,
  midiToSolfege,
} from './liveScoringView';
import { ScoreOverlay } from './ScoreOverlay';
import {
  VERDICT_LABEL,
  formatCents,
  formatPercent,
  midiToNoteName,
  verdictToneClass,
} from './scoringView';

import type { MelodyProgress } from './liveScoringView';
import type { OverlayPoint } from './scoreOverlayGeometry';

/**
 * LiveScoreView(#8) — 세션 running 중 실시간 피드백. 정지 후 요약/오버레이로 가기
 * 전, 패널에 (1) 현재 목표 음 + 진행도, (2) 라이브 판정 + cents 바늘, (3) 러닝
 * 정확도·in-tune, (4) 플레이헤드가 흐르는 목표 vs 실제 오버레이를 채운다.
 *
 * 점수/진행도 계산은 모두 순수 헬퍼(`melodyProgress`, `summarizeSession`) — raw f0만
 * 쓰고 표시 스무딩 값은 점수에 넣지 않는다. 여긴 그 결과를 그릴 뿐이다.
 */

const fmtSeconds = (ms: number): string => `${(ms / 1000).toFixed(1)}s`;

const TargetCard = ({ progress }: { progress: MelodyProgress }) => {
  const { activeNote, nextNote } = progress;
  const position = `${Math.min(progress.started, progress.total)}/${progress.total}`;

  return (
    <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/[0.03] p-3">
      {activeNote ? (
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-3xl font-bold text-foreground">
            {midiToNoteName(activeNote.midi)}
          </span>
          <span className="text-lg text-primary">
            {midiToSolfege(activeNote.midi)}
          </span>
          {activeNote.lyric && (
            <span className="text-sm text-muted-foreground">
              “{activeNote.lyric}”
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-col">
          <span className="text-lg font-semibold text-muted-foreground">
            쉼
          </span>
          {nextNote && progress.msToNextNote !== null && (
            <span className="text-xs text-muted-foreground">
              다음 {midiToNoteName(nextNote.midi)} ·{' '}
              {fmtSeconds(progress.msToNextNote)} 후
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col items-end">
        <span className="font-mono text-sm text-foreground">{position}</span>
        {activeNote && (
          <span className="text-[11px] text-muted-foreground">
            남은 {fmtSeconds(progress.remainingInNoteMs)}
          </span>
        )}
      </div>
    </div>
  );
};

const CentsNeedle = ({ score }: { score: FrameScore }) => {
  const offset = centsToOffsetPct(score.centsDiff);
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-sm font-semibold ${verdictToneClass(
          score.verdict,
        )}`}
      >
        {VERDICT_LABEL[score.verdict]}
      </span>
      <div className="relative h-6 flex-1 rounded-md bg-muted/40">
        <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border" />
        {offset !== null && (
          <div
            className="absolute top-1/2 h-4 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary transition-[left] duration-75"
            style={{ left: `${(offset + 100) / 2}%` }}
          />
        )}
      </div>
      <span className="w-12 shrink-0 text-right font-mono text-xs text-muted-foreground">
        {formatCents(score.centsDiff)}
      </span>
    </div>
  );
};

const RunningStat = ({
  label,
  value,
  tip,
}: {
  label: string;
  value: string;
  tip: string;
}) => (
  <div className="flex flex-col gap-0.5 rounded-md border border-border bg-card p-2">
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      {label}
      <InfoTip label={`${label} 설명`}>{tip}</InfoTip>
    </span>
    <span className="font-mono text-sm text-foreground">{value}</span>
  </div>
);

export const LiveScoreView = ({
  melody,
  atMs,
  score,
  frames,
  actual,
}: {
  melody: TargetMelody;
  atMs: number;
  score: FrameScore;
  frames: ScoredFrame[];
  actual: OverlayPoint[];
}) => {
  const progress = melodyProgress(melody, atMs);
  const running = summarizeSession(frames, melody);

  return (
    <div className="flex flex-col gap-3">
      <TargetCard progress={progress} />
      <CentsNeedle score={score} />
      <div className="grid grid-cols-2 gap-2">
        <RunningStat
          label="정확도"
          value={formatPercent(running.accuracyPct)}
          tip="지금까지 판정된 프레임 중 음정이 맞은 비율입니다(놓침 포함)."
        />
        <RunningStat
          label="in-tune 비율"
          value={formatPercent(running.inTuneRatio * 100)}
          tip="소리를 낸 프레임 중 정확했던 비율입니다(놓침 제외)."
        />
      </div>
      <ScoreOverlay melody={melody} actual={actual} playheadMs={atMs} />
    </div>
  );
};
